import {
  Directive,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { RbacService, RbacAction, RbacModuleKey } from '../services/rbac.service';

type CanInput =
  | string // "contracts:read" OU "regions|areas|routes:read" OU "regions,areas,routes:read"
  | [RbacModuleKey, RbacAction] // ['contracts','read']
  | [RbacModuleKey[], RbacAction]; // [['regions','areas','routes'],'read']

@Directive({
  selector: '[appCan]',
})
export class AppCanDirective implements OnInit, OnDestroy {
  private sub?: Subscription;
  private rendered = false;

  private modules: RbacModuleKey[] = [];
  private action?: RbacAction;

  @Input('appCan')
  set appCan(value: CanInput) {
    const parsed = this.parse(value);
    this.modules = parsed?.modules ?? [];
    this.action = parsed?.action;
    this.updateView();
  }

  constructor(
    private tpl: TemplateRef<any>,
    private vcr: ViewContainerRef,
    private rbac: RbacService
  ) {}

  ngOnInit(): void {
    this.sub = this.rbac.state$.subscribe(() => this.updateView());
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private parse(value: CanInput): { modules: RbacModuleKey[]; action: RbacAction } | null {
    if (!value) return null;

    // string: "a:read" ou "a|b|c:read"
    if (typeof value === 'string') {
      const [modsRaw, actRaw] = value.split(':').map(s => s?.trim());
      if (!modsRaw || !actRaw) return null;

      const mods = modsRaw
        .split(/[|,]/g) // aceita "|" ou ","
        .map(s => s.trim())
        .filter(Boolean) as RbacModuleKey[];

      if (!mods.length) return null;
      return { modules: mods, action: actRaw as RbacAction };
    }

    // tuple: ['contracts','read']
    if (Array.isArray(value) && value.length === 2) {
      const [mOrList, a] = value as any;
      if (!mOrList || !a) return null;

      const mods = Array.isArray(mOrList) ? mOrList : [mOrList];
      const clean = (mods as RbacModuleKey[]).filter(Boolean);

      if (!clean.length) return null;
      return { modules: clean, action: a as RbacAction };
    }

    return null;
  }

  private updateView(): void {
    if (!this.modules.length || !this.action) {
      this.clear();
      return;
    }

    // OR: renderiza se QUALQUER módulo permitir
    const allowed = this.modules.some(m => this.rbac.can(m, this.action!));

    if (allowed && !this.rendered) {
      this.vcr.createEmbeddedView(this.tpl);
      this.rendered = true;
    } else if (!allowed && this.rendered) {
      this.clear();
    }
  }

  private clear(): void {
    this.vcr.clear();
    this.rendered = false;
  }
}
